import { API_ENDPOINTS } from "../constants/apiEndpoints";
import { HttpMethod, HTTP_METHODS } from "../constants/httpMethods";
import { getLocalStorageKey, removeLocalStorageKey, setLocalStorageKey } from "../helpers/localStorageHelper";
import { ApiResponse } from "../interfaces/responses/ApiResponse";

export interface ApiServiceOptions {
  url: string;
  method?: HttpMethod;
  body?: Record<string, unknown>;
  headers?: Record<string, string>;
  params?: Record<string, string | number>;
  baseUrl?: string;
}

// Backend errors always come back as an ApiResponse<T> JSON body
// ({success:false, message, errors}) even on a non-2xx status -- read that
// instead of just the generic HTTP status text, so callers see e.g.
// "EMAIL_NOT_CONFIRMED" or "Invalid credentials" rather than
// "HTTP Error: 403 - Forbidden".
const parseErrorMessage = async (response: Response): Promise<string> => {
  // ASP.NET Core's rate limiter middleware rejects with an empty body (no
  // ApiResponse JSON to read a message from), unlike every other error path
  // here -- return a sentinel callers can match on instead of falling
  // through to the generic "HTTP Error: 429 - Too Many Requests" below.
  if (response.status === 429) return 'RATE_LIMITED';

  try {
    const body = await response.json();
    if (body?.message) return body.message;
    if (Array.isArray(body?.errors) && body.errors.length > 0) return body.errors[0];
  } catch {
    // Body wasn't JSON -- fall through to the generic message below.
  }
  return `HTTP Error: ${response.status} - ${response.statusText}`;
};

// A 2xx with no body at all. Most of this API answers even a delete with a
// JSON ApiResponse, but a few endpoints reply 204 No Content on purpose
// (PublicController.CreateSignupLead, ServiceController's compatibility
// delete) -- and response.json() throws on an empty body, which would turn
// a call that worked into a call the caller reports as failed. Read the text
// first and only parse it if there is any.
const parseOkBody = async <T>(response: Response): Promise<T> => {
  const text = await response.text();
  if (!text) return {} as T;
  return JSON.parse(text) as T;
};

// Main API service with refresh token handling
export const apiService = async <T>({
  url,
  method = HTTP_METHODS.GET,
  body,
  headers,
  params,
  baseUrl: customBaseUrl = "",
}: ApiServiceOptions): Promise<ApiResponse<T>> => {
  try {
    // Default headers
    const defaultHeaders: Record<string, string> = {
      "Content-Type": "application/json",
    };

    // Merge default headers with provided headers
    const mergedHeaders: Record<string, string> = { ...defaultHeaders, ...(headers || {}) };

    // Append query parameters and prepend the base URL
    const fullUrl = appendQueryParams(`${customBaseUrl}${url}`, params);

    const accessToken = getLocalStorageKey("accessToken");

    // Attach Authorization header if access token exists
    if (accessToken) {
      mergedHeaders.Authorization = `Bearer ${accessToken}`;
    }

    // Make the first request
    const response = await fetch(fullUrl, {
      method,
      headers: mergedHeaders,
      body: body ? JSON.stringify(body) : undefined,
    });

    // Handle 401 Unauthorized (token expired)
    if (response.status === 401) {
      const refreshSuccess = await handleTokenRefresh();
      if (refreshSuccess) {
        // Retry the original request with the new access token
        const newAccessToken = getLocalStorageKey("accessToken");
        if (newAccessToken) {
          mergedHeaders.Authorization = `Bearer ${newAccessToken}`;

          const retryResponse = await fetch(fullUrl, {
            method,
            headers: mergedHeaders,
            body: body ? JSON.stringify(body) : undefined,
          });

          if (retryResponse.ok) {
            const parsedRetryResponse = await parseOkBody<T>(retryResponse);
            return { success: true, data: parsedRetryResponse };
          }
        }
      }

      // Refresh failed or retry failed, redirect to login
      redirectToLogin();
      return { success: false, error: "Unauthorized - Redirecting to login", data: {} as T };
    }

    // If the response is ok, parse the response
    if (response.ok) {
      const parsedResponse = await parseOkBody<T>(response);
      return  { success: true, data: parsedResponse };
    }

    // Handle other HTTP errors -- read the backend's own message instead
    // of just the generic HTTP status text.
    return { success: false, error: await parseErrorMessage(response), data: {} as T, status: response.status };
  } catch (error) {
    return { success: false, error: (error as Error).message, data: {} as T };
  }
};

export interface ApiUploadOptions {
  url: string;
  formData: FormData;
  params?: Record<string, string | number>;
  baseUrl?: string;
}

// Same auth/refresh handling as apiService(), but sends a FormData body
// without a Content-Type header so the browser sets the multipart boundary.
export const apiUploadService = async <T>({
  url,
  formData,
  params,
  baseUrl: customBaseUrl = "",
}: ApiUploadOptions): Promise<ApiResponse<T>> => {
  try {
    const headers: Record<string, string> = {};

    const fullUrl = appendQueryParams(`${customBaseUrl}${url}`, params);
    const accessToken = getLocalStorageKey("accessToken");
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

    const response = await fetch(fullUrl, { method: HTTP_METHODS.POST, headers, body: formData });

    if (response.status === 401) {
      const refreshSuccess = await handleTokenRefresh();
      if (refreshSuccess) {
        const newAccessToken = getLocalStorageKey("accessToken");
        if (newAccessToken) {
          headers.Authorization = `Bearer ${newAccessToken}`;
          const retryResponse = await fetch(fullUrl, { method: HTTP_METHODS.POST, headers, body: formData });
          if (retryResponse.ok) {
            const parsedRetryResponse = await parseOkBody<T>(retryResponse);
            return { success: true, data: parsedRetryResponse };
          }
        }
      }
      redirectToLogin();
      return { success: false, error: "Unauthorized - Redirecting to login", data: {} as T };
    }

    if (response.ok) {
      const parsedResponse = await parseOkBody<T>(response);
      return { success: true, data: parsedResponse };
    }

    return { success: false, error: await parseErrorMessage(response), data: {} as T, status: response.status };
  } catch (error) {
    return { success: false, error: (error as Error).message, data: {} as T };
  }
};

// Utility function to append query parameters
const appendQueryParams = (url: string, params?: Record<string, string | number>): string => {
  if (!params) return url;

  const queryParams = new URLSearchParams(
    Object.entries(params).map(([key, value]) => [key, String(value)])
  ).toString();

  return `${url}?${queryParams}`;
};


export const handleTokenRefresh = async (): Promise<boolean> => {
  const refreshToken = getLocalStorageKey("refreshToken");
  const expiredToken = getLocalStorageKey("accessToken");

  if (!refreshToken || !expiredToken) {
    return false;
  }

  try {
    // Refresh-token now lives on HeavyDutyMachineryApi's own AuthController --
    // every caller of apiService() across the whole app relies on this for
    // its automatic 401 retry, not just auth-specific calls, so getting this
    // baseUrl wrong breaks everything on the first expired token, not just
    // login-related screens.
    const refreshBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || "";
    const response = await fetch(`${refreshBaseUrl}${API_ENDPOINTS.REFRESH_TOKEN}`, {
      method: HTTP_METHODS.POST,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        token: expiredToken, // Send the expired access token
        refreshToken: refreshToken, // Send the refresh token
      }),
    });

    if (response.ok) {
      const parsedResponse = await response.json();

      if (parsedResponse.success) {
        const { token: newAccessToken, refreshToken: newRefreshToken } = parsedResponse.data;

        // Update the tokens in localStorage
        setLocalStorageKey("accessToken", newAccessToken);
        setLocalStorageKey("refreshToken", newRefreshToken);

        return true;
      }
    }
  } catch (error) {
    console.error("Failed to refresh token:", error);
  }

  return false;
};



// Redirect to login
const redirectToLogin = () => {
  removeLocalStorageKey("accessToken");
  removeLocalStorageKey("refreshToken");
  window.location.href = "/login";
};
